import type { ChatMessage, ContentItem, TodoListContentItem, TodoTask } from '../shared/interfaces';

// Cards de lista de tarefas (lista_de_tarefas) ao longo do chat. Um card
// parcial só interessa enquanto for o retrato mais recente: a mesma lista
// atualizada num turno posterior deixa o anterior como um estágio velho. Fica
// visível quem está 100% concluído (um final) ou quem é o último do chat.

export function isTodoFinal(item: TodoListContentItem): boolean {
  const tarefas = item.tarefas || [];
  return tarefas.length > 0 && tarefas.every((t) => t.status === 'concluida');
}

export function lastTodoMessageIndex(messages: Array<Pick<ChatMessage, 'role' | 'content'>>): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.role !== 'assistant' || !Array.isArray(m.content)) continue;
    if (m.content.some((it: any) => it?.type === 'todo_list')) return i;
  }
  return -1;
}

// `superseded`: existe um card mais recente noutra mensagem do chat.
export function shouldShowTodoCard(item: TodoListContentItem, superseded: boolean): boolean {
  return !superseded || isTodoFinal(item);
}

// A linha do assistente só monta se tiver algo visível: um turno que, por
// enquanto, só tem o checklist precisa contar como conteúdo.
export function hasVisibleTodoCard(content: string | ContentItem[] | undefined, superseded: boolean): boolean {
  if (!Array.isArray(content)) return false;
  return content.some((it: any) => it?.type === 'todo_list' && shouldShowTodoCard(it as TodoListContentItem, superseded));
}

// Aplica um evento todo_list ao histórico: substitui o card da última mensagem
// do assistente ou, se o turno ainda não tem mensagem do assistente (retomada
// em background com o checklist antes de texto/raciocínio), cria o scaffold.
export function applyTodoToHistory<M extends { role: string; content: any }>(prev: M[], tarefas: TodoTask[]): M[] {
  if (prev.length === 0) return prev;
  const lastEntry = prev[prev.length - 1];
  const isAssistant = lastEntry.role === 'assistant';
  const lastMsg: any = isAssistant ? { ...lastEntry } : { role: 'assistant', content: [], references: [] };
  const content: any[] = Array.isArray(lastMsg.content)
    ? lastMsg.content.filter((it: any) => it?.type !== 'todo_list')
    : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);
  content.push({ type: 'todo_list', tarefas });
  const next = { ...lastMsg, content } as M;
  return isAssistant ? [...prev.slice(0, -1), next] : [...prev, next];
}
